import AvatarTemplate from "../../assets/images/AvatarTemplate.png"
import { handleTableId } from "../../helpers/helper"
import { getContactProfile } from "../../features/customer/customerSlice"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { formatMoney } from "../../utils/amount"
import { BackArrow } from "../../components/Icons/Icons"
import PopupWithActions from "../../components/Popup/PopupWithAction"
import AddAlertModal from "../../components/AccountAlerts/AddAlertModal"
import SendPaymentLinkModal from "./SendPaymentLinkModal"
import QcCallReviewDrawer from "./QcCallReviewDrawer/QcCallReviewDrawer"
import { getQcCallResults } from "../../features/qcCall/qcCall"
import { LuKey, LuTrash2 } from "react-icons/lu"
import StatusBadge from "../../components/StatusBadge/StatusBadge"
import SuspendedBanner from "../../components/SuspendedBanner/SuspendedBanner"
import { acceptsCard } from "../../constants/paymentMethods"
import RecordFinancingModal from "./Financing/RecordFinancingModal"
import FinancedBadge from "./Financing/FinancedBadge"
import FinancingStatusBadge from "./Financing/FinancingStatusBadge"
import ChargeNameBadge from "../../components/LsaChargeName/ChargeNameBadge"
import dayjs from "dayjs"
import { businessTimeZone } from "../../utils/date"
import DeleteContactDialog from "../../components/DeleteContactDialog/DeleteContactDialog"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { CONTACT_SOFT_DELETE } from "../../config/featureFlagKeys"


// financingEnabled / canRecordFinancing / onFinancingRecorded: WS 2211251, read by Contact.jsx.
// financingPendingEnabled: WS 2311820; onFinancingRecorded also reloads after the rep's "Lender funded".
const ContactProfile = ({ user, userId, triggerUpdateSidebar, userRole, onAlertSaved, financingEnabled = false, financingPendingEnabled = false, canRecordFinancing = false, onFinancingRecorded, backTo, lsaChargeNameEnabled = false }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [profile, setProfile] = useState({
    totalDays: 0,
    paidAmount: 0,
    userStatus: null,
  })

  // Three-dots menu state
  const [anchorMenu, setAnchorMenu] = useState(null)
  const [openMenu, setOpenMenu] = useState(false)
  const [showAddAlert, setShowAddAlert] = useState(false)
  const [showPaymentLink, setShowPaymentLink] = useState(false)
  const [showRecordFinancing, setShowRecordFinancing] = useState(false)
  const [recordFinancingDefaults, setRecordFinancingDefaults] = useState(null)
  const [showDeleteClient, setShowDeleteClient] = useState(false)
  const deleteClientEnabled = useSelector(selectFeatureFlag(CONTACT_SOFT_DELETE))

  const canManageAlerts = userRole === 'customer' || userRole === 'user'
  const canDeleteClient = userRole === 'customer' && deleteClientEnabled
  const isDuplicate = user?.statusData?.value === 'duplicate'
  const portalEmailAmbiguous = !!profile?.portalEmailAmbiguous
  const isFinanced = !!user?.financing?.recordedAt
  const offerRecordFinancing = financingEnabled && canRecordFinancing && !!user && !isFinanced

  const [showQcDrawer, setShowQcDrawer] = useState(false)
  const [hasQcNeedsReview, setHasQcNeedsReview] = useState(false)
  const [hasQcCalls, setHasQcCalls] = useState(false)

  const refreshQcReviewFlag = async () => {
    try {
      const res = await dispatch(getQcCallResults(userId))
      const list = (res.payload && res.payload.data) || []
      setHasQcCalls(list.length > 0)
      setHasQcNeedsReview(list.some((r) => r.reviewStatus === 'needs_review'))
    } catch (error) {
      setHasQcCalls(false)
      setHasQcNeedsReview(false)
    }
  }

  useEffect(() => {
    if (canManageAlerts && userId) {
      refreshQcReviewFlag()
    }
  }, [canManageAlerts, userId])

  const handleReListenClick = () => {
    handleCloseMenu()
    setShowQcDrawer(true)
  }

  const handleQcDrawerClose = () => {
    setShowQcDrawer(false)
    if (canManageAlerts && userId) refreshQcReviewFlag()
    if (onAlertSaved) onAlertSaved()
  }

  const handleMenuClick = (event) => {
    setAnchorMenu(event.currentTarget)
    setOpenMenu(true)
  }

  const handleCloseMenu = () => {
    setOpenMenu(false)
    setAnchorMenu(null)
  }

  const handleAddAlertClick = () => {
    setShowAddAlert(true)
    handleCloseMenu()
  }

  const handleResetPasswordClick = () => {
    handleCloseMenu()
    const email = encodeURIComponent(user?.secondaryEmail || user?.email || '')
    navigate(`/admin/reset-password?userId=${userId}&userType=customer&email=${email}`)
  }

  const handleSendPaymentLinkClick = () => {
    setShowPaymentLink(true)
    handleCloseMenu()
  }

  const handleRecordFinancingClick = () => {
    setRecordFinancingDefaults(null)
    setShowRecordFinancing(true)
    handleCloseMenu()
  }

  const handleDeleteClientClick = () => {
    handleCloseMenu()
    setShowDeleteClient(true)
  }

  // "Mark paid" on the financing-pending badge: Record financing with the expected amount and today.
  const handleMarkPaid = ({ amount }) => {
    setRecordFinancingDefaults({ amount, fundedAt: dayjs().tz(businessTimeZone).format('YYYY-MM-DD') })
    setShowRecordFinancing(true)
  }

  const handleAlertClose = () => {
    setShowAddAlert(false)
  }

  const handleAlertSaved = () => {
    setShowAddAlert(false)
    if (onAlertSaved) onAlertSaved()
  }

  const fetchData = async () => {
    try {
      const response = await dispatch(getContactProfile({ userId }))
      if (response?.payload?.data) {
        setProfile(response?.payload?.data)
      }
    } catch (error) {
      console.error('Error dispatching getContactById:', error)
    }
  }

  useEffect(() => {
    fetchData()
  }, [userId, triggerUpdateSidebar])

  return (
    <>
      <SuspendedBanner statusValue={user?.statusData?.value} />
      {portalEmailAmbiguous && !isDuplicate && (
        <div className="mb-2 px-4 py-2 rounded-lg bg-warning-light border border-warning-main text-warning-main text-sm" role="alert">
          This email is shared by more than one account — a password reset may not land where the portal login resolves. Resolve the duplicate first.
        </div>
      )}
      <div className="flex justify-between items-center bg-paper border border-divider rounded-2xl shadow-sm">
      <div className="flex justify-start items-center">
        <button onClick={() => navigate(backTo || -1)} className="ml-4 cursor-pointer hover:opacity-70">
          <BackArrow />
        </button>
        <div className="flex shrink-0 pl-10 pr-8 py-3 items-center">
          <img src={AvatarTemplate} alt="photo" className="w-10 h-10 rounded-full mr-4" />
          <div className="flex flex-col">
            <span className="font-bold text-text-primary">{(user?.firstName || '') + " " + (user?.lastName || '')}</span>
            <span
              className="text-text-secondary whitespace-nowrap">ID: {user?._id ? handleTableId(user?._id) : ''}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-text-secondary font-bold text-md mr-6 whitespace-nowrap">
          Status:
          <StatusBadge
            label={profile?.userStatus?.label}
            bgColor={profile?.userStatus?.bgColor}
            textColor={profile?.userStatus?.textColor}
            width="min-w-[90px]"
          />
          {financingEnabled && <FinancedBadge financing={user?.financing} />}
          {financingPendingEnabled && (
            <FinancingStatusBadge userId={userId} financingState={user?.financingState}
              canMarkPaid={offerRecordFinancing} onMarkPaid={handleMarkPaid} onChanged={onFinancingRecorded} />
          )}
          {lsaChargeNameEnabled && <ChargeNameBadge userId={userId} />}
        </div>
        <div className="text-text-secondary font-bold text-md whitespace-nowrap">Days: {profile?.totalDays || 0}</div>
        <div className="text-text-secondary font-bold text-md ml-6 whitespace-nowrap">Total Paid: {formatMoney(profile?.paidAmount)}</div>
      </div>
       {/* Three-dots menu */}
      {canManageAlerts && (
        <button
          onClick={handleMenuClick}
          className="relative mr-2 p-1 rounded-full hover:bg-divider/50 transition-colors cursor-pointer"
        >
          <svg className="w-5 h-5 text-text-secondary" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
          {hasQcNeedsReview && <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full" />}
        </button>
      )}
      {/* Popup Menu */}
      <PopupWithActions
        shadow="shadow-sm"
        anchor={anchorMenu}
        open={openMenu}
        handleClose={handleCloseMenu}
      >
        <div
          onClick={handleAddAlertClick}
          className="px-4 py-2 cursor-pointer hover:bg-divider/50 text-text-primary text-sm flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Alert
        </div>
        {userRole === 'customer' && (
          <div
            onClick={isDuplicate ? undefined : handleResetPasswordClick}
            title={isDuplicate ? 'Record is a duplicate — reset the password on the primary record' : undefined}
            className={`px-4 py-2 text-sm flex items-center gap-2 ${isDuplicate ? 'opacity-50 cursor-not-allowed text-text-secondary' : 'cursor-pointer hover:bg-divider/50 text-text-primary'}`}
          >
            <div className="w-4 h-4 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4">
              <LuKey size={18} className={isDuplicate ? 'text-text-secondary' : 'text-text-primary'} />
            </div>
            Reset Password
          </div>
        )}
        {userRole === 'customer' && acceptsCard(user?.acceptedPaymentMethods) && (
          <div
            onClick={handleSendPaymentLinkClick}
            className="px-4 py-2 cursor-pointer hover:bg-divider/50 text-text-primary text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5" />
            </svg>
            Send Payment Link
          </div>
        )}
        {offerRecordFinancing && (
          <div
            onClick={handleRecordFinancingClick}
            className="px-4 py-2 cursor-pointer hover:bg-divider/50 text-text-primary text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Record financing
          </div>
        )}
        {hasQcCalls && (
          <div
            onClick={handleReListenClick}
            className="px-4 py-2 cursor-pointer hover:bg-divider/50 text-text-primary text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
            </svg>
            <span className="flex items-center gap-2">Re-listen QC Call{hasQcNeedsReview && <span className="w-2 h-2 bg-red-500 rounded-full" />}</span>
          </div>
        )}
        {canDeleteClient && (
          <div
            onClick={handleDeleteClientClick}
            className="px-4 py-2 cursor-pointer hover:bg-divider/50 text-error-main text-sm flex items-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4">
              <LuTrash2 size={18} className="text-error-main" />
            </div>
            Delete client
          </div>
        )}
      </PopupWithActions>

      {/* Add Alert Modal */}
      <AddAlertModal
        userId={userId}
        externalOpen={showAddAlert}
        hideButton={true}
        onClose={handleAlertClose}
        onSaved={handleAlertSaved}
      />

      <QcCallReviewDrawer open={showQcDrawer} userId={userId} onClose={handleQcDrawerClose} />

      {canDeleteClient && (
        <DeleteContactDialog
          open={showDeleteClient}
          userId={userId}
          onClose={() => setShowDeleteClient(false)}
          onDeleted={(impact) => navigate(impact?.isLead ? '/leads' : '/customers')}
        />
      )}

      {/* ModalComponent leaves a min-w-[520px] wrapper in the page even while closed; the dialog
          itself is portalled, so hiding the wrapper keeps the header on one row. */}
      <div className="hidden">
        <SendPaymentLinkModal
          userId={userId}
          open={showPaymentLink}
          onClose={() => setShowPaymentLink(false)}
        />
      </div>

      {offerRecordFinancing && (
        <div className="hidden">
          <RecordFinancingModal
            userId={userId}
            open={showRecordFinancing}
            defaults={recordFinancingDefaults}
            onClose={() => setShowRecordFinancing(false)}
            onRecorded={() => {
              setShowRecordFinancing(false)
              if (onFinancingRecorded) onFinancingRecorded()
            }}
          />
        </div>
      )}
      </div>
    </>
  )
}


export default ContactProfile
